import { ErrorResponse } from '@apollo/client/link/error';
import { FullStory, isInitialized } from '@fullstory/browser';
import { AxiosError } from 'axios';
import * as R from 'ramda';

import { Snackbar } from '@atom/mui';
import configuration, { LOCAL } from './configuration';

// LOCAL_MOCK: skip redirects/snackbars on API errors when running against
// localhost without backends. Lets prototype screens fail silently instead of
// bouncing the user to /not-found or /unknown-error.
const LOCAL_MOCK = configuration.env === LOCAL;

const GQL_OPERATION_DEFINITION = 'OperationDefinition';

// Any routes that the interceptors should ignore completely.
// This list will be used to regex match against the route and
// remove it from the error handler flow.
const ignoredRoutes = ['/profile'];

enum HTTPMethods {
  GET = 'get',
  POST = 'post',
  PATCH = 'patch',
  PUT = 'put',
  DELETE = 'delete',
}

enum GQLMethods {
  QUERY = 'query',
  MUTATION = 'mutation',
}

export const handleRetrieveMethodErrors = (status: number) => {
  switch (true) {
    case status === 403:
      window.location.href = '/no-permission';
      return;
    case status === 404:
      window.location.href = '/not-found';
      return;
    case status >= 500:
      window.location.href = '/unknown-error';
      return;
    default:
      return;
  }
};

export const handleActionMethodErrors = (status: number) => {
  switch (true) {
    case status === 403:
      return Snackbar.error({
        message: `You don't have permission to complete this action.`,
      });
    case status === 404:
      return Snackbar.error({
        message: `The resource could not be found. It may have been deleted.`,
      });
    default:
      return '';
  }
};

export const handleErrors = (
  method: HTTPMethods | GQLMethods,
  status: number,
) => {
  if (LOCAL_MOCK) {
    return '';
  }
  switch (method) {
    case GQLMethods.QUERY:
      return handleRetrieveMethodErrors(status);
    case HTTPMethods.GET:
      return handleRetrieveMethodErrors(status);
    case GQLMethods.MUTATION:
      handleActionMethodErrors(status);
      return '';
    case HTTPMethods.POST:
      handleActionMethodErrors(status);
      return '';
    case HTTPMethods.PATCH:
      handleActionMethodErrors(status);
      return '';
    case HTTPMethods.PUT:
      handleActionMethodErrors(status);
      return '';
    case HTTPMethods.DELETE:
      handleActionMethodErrors(status);
      return '';
    default:
      return '';
  }
};

const containsIgnoredRoute = (route: string) => {
  return ignoredRoutes.reduce((acc, ignoredRoute) => {
    return acc || R.includes(ignoredRoute, route);
  }, false);
};

export const interceptAxiosError = (error: AxiosError) => {
  if (error && error?.isAxiosError) {
    const doesNotContainIgnoredRoute = !containsIgnoredRoute(
      error?.request?.responseURL,
    );

    const status = error?.response?.status;
    const method = error?.config?.method;

    if (isInitialized()) {
      FullStory('trackEvent', {
        name: 'HTTP Error',
        properties: {
          url: error?.config?.url ?? 'unknown',
          status: status ?? 0,
          method: method ?? 'unknown',
        },
      });
    }

    if (method && status && doesNotContainIgnoredRoute) {
      // @ts-ignore
      handleErrors(method, status);
    }
  }
};

export const interceptApolloError = (error: ErrorResponse) => {
  // @ts-ignore
  const status = error?.networkError?.statusCode;
  const method = error?.operation?.query?.definitions.find(
    definition => definition.kind === GQL_OPERATION_DEFINITION,
  )?.operation;

  if (isInitialized()) {
    FullStory('trackEvent', {
      name: 'GraphQL Error',
      properties: {
        operation: error?.operation?.operationName ?? 'unknown',
        status: status ?? 0,
        method: method ?? 'unknown',
      },
    });
  }

  if (method && status) {
    // @ts-ignore
    handleErrors(method, status);
  }
};
